import { useEffect, useMemo, useState } from 'react';
import { 
  useCreateContractContext,
  useErrorHandlerContext,
  useOpeningElementsControllerContext,
  useUserLanguage,
} from '@/customHooks';
import { capitalizeFirstLetter, genderMapper } from '@/helpers';
import { OrderDirectionOption, PageSizeOption, useGetTenantsForContractsQuery } from '@/graphql/generated';
import { TenantSimpleListData } from '@/custom-types';
import { LoadingSpinner } from '../atoms';

type TenantSimpleSelectionListProps = {
  selected: (tenant: TenantSimpleListData | null) => void
  search: string
  orderDirection: OrderDirectionOption
  currentPage: number
  pageSize: PageSizeOption
}

const textsData = {
  en: {
    name: 'Name',
    email: 'Email',
    gender: 'Gender',
  },
  es: {
    name: 'Nombre',
    email: 'Correo',
    gender: 'Género'
  }
}

const dataFlatter = (data: TenantSimpleListData[] | undefined, userLanguage: 'es' | 'en') => {
  if (!data || data.length === 0) return []

  return data.map((tenant) => {
    return {
      id: tenant.user.id,
      firstName: tenant.firstName,
      lastName: tenant.lastName,
      email: tenant.user.email,
      gender: genderMapper(tenant.genderOption, userLanguage),
    }
  })
}

export const TenantSimpleSelectionList = ({ selected, search, orderDirection, currentPage, pageSize }: TenantSimpleSelectionListProps) => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const { state } = useOpeningElementsControllerContext()

  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { setButtonDisabled, setButtonEnabled } = useCreateContractContext()
  
  const { open: errorHandle } = useErrorHandlerContext()

  const variables = useMemo(() => ({
    input: {
      search: search || undefined,
      orderDirection,
      page: currentPage.toString(),
      pageSize,
    }
  }), [orderDirection, search, currentPage, pageSize])

  const { data, error, loading, refetch } = useGetTenantsForContractsQuery({
    variables: variables as any,
    fetchPolicy: 'network-only'
  })

  if (error) errorHandle(error);

  const tableData = useMemo(() => dataFlatter(data?.tenants?.items, userLanguage), [data, userLanguage])

  useEffect(() => {
    if (!data?.tenants?.items) return
    const tenant = data.tenants.items.find((tenant) => selectedId === tenant.user.id)
    selected(tenant ?? null)
  }, [selectedId, data, selected])

  useEffect(() => {
    if (loading) setButtonDisabled()
    else setButtonEnabled()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  useEffect(() => {
    if(!state.isCreateTenantFormModalOpen) refetch()

  }, [state.isCreateTenantFormModalOpen, refetch])
  
  return (
    <div className="py-6 w-full">
      {loading ? (
        <div className="flex justify-center">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="rounded-xl grid grid-cols-[64px_2fr_2fr_1fr] content-start w-full max-w-full min-w-[300px] max-h-[80vh] overflow-y-auto min-[500px]:min-w-[500px]">
          {/* Encabezado */}
          <div className="contents">
            <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10"></div>
            <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">{texts.name}</div>
            <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">{texts.email}</div>
            <div className="font-bold text-left p-[15px] border-t border-b border-gray-500 bg-white sticky top-0 z-10">{texts.gender}</div>
          </div>

          {/* Filas dinámicas */}
          {tableData.map((tenant) => (
            <div key={tenant.id} className="contents group">
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
                <input
                  type="radio"
                  checked={selectedId === tenant.id}
                  onChange={() => setSelectedId(tenant.id)}
                  className="cursor-pointer"
                />
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
                {`${capitalizeFirstLetter(tenant.firstName)} ${capitalizeFirstLetter(tenant.lastName)}`}
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
                {tenant.email}
              </div>
              <div className="p-[15px] text-sm flex items-center border-b border-gray-200 group-odd:bg-white group-even:bg-[--color-background-2] min-h-[50px]">
                {tenant.gender}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
